import { useRef, useState } from "react";
import { Upload, FileText, XCircle, Loader } from "lucide-react";

const UploadModal = ({
  t,
  styles,
  theme,
  language,
  selectedFile,
  setSelectedFile,
  selectedType,
  setSelectedType,
  setShowUploadModal,
  handleUpload,
  isUploading,
  ReusableDropdown,
}) => {
  const fileInputRef = useRef(null);
  const [otherDocumentName, setOtherDocumentName] = useState("");

  const handleClose = () => {
    if (isUploading) return;
    setShowUploadModal(false);
    setSelectedFile(null);
  };

  return (
    <div style={styles.modalOverlay} onClick={handleClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{t.uploadNewDocument}</h3>
          <button
            style={styles.closeButton}
            onClick={handleClose}
            disabled={isUploading}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                theme === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.06)";
            }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
          >
            <XCircle size={28} />
          </button>
        </div>

        {/* Drop Zone */}
        <div
          style={styles.uploadArea}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#3b82f6";
            e.currentTarget.style.background =
              theme === "dark" ? "rgba(99, 102, 241, 0.15)" : "rgba(59, 130, 246, 0.1)";
            e.currentTarget.style.boxShadow =
              theme === "dark"
                ? "0 15px 40px rgba(0, 0, 0, 0.4), 0 8px 20px rgba(59, 130, 246, 0.2)"
                : "0 15px 40px rgba(0, 0, 0, 0.08), 0 8px 20px rgba(59, 130, 246, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor =
              theme === "dark" ? "rgba(99, 102, 241, 0.5)" : "rgba(59, 130, 246, 0.5)";
            e.currentTarget.style.background =
              theme === "dark" ? "rgba(99, 102, 241, 0.08)" : "rgba(59, 130, 246, 0.05)";
            e.currentTarget.style.boxShadow =
              theme === "dark"
                ? "0 10px 30px rgba(0, 0, 0, 0.3)"
                : "0 10px 30px rgba(0, 0, 0, 0.06)";
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload size={64} color="#3b82f6" style={styles.uploadIcon} />
          <h3 style={styles.uploadTitle}>{t.dragDropFiles}</h3>
          <p style={styles.uploadSubtitle}>{t.orClickBrowse}</p>
          <button
            style={{
              ...styles.primaryButton,
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              margin: "20px auto 0",
            }}
            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
          >
            <Upload size={20} />
            {t.browseFiles}
          </button>
          <input
            type="file"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={(e) => setSelectedFile(e.target.files[0])}
          />
        </div>

        {/* Selected File Preview */}
        {selectedFile && (
          <div
            style={{
              padding: "20px",
              background: theme === "dark" ? "rgba(59, 130, 246, 0.12)" : "rgba(59, 130, 246, 0.08)",
              borderRadius: "16px",
              marginBottom: "28px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              border: `2px solid ${theme === "dark" ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"}`,
              boxShadow: "0 6px 18px rgba(59, 130, 246, 0.15)",
            }}
          >
            <FileText size={32} color="#3b82f6" />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: theme === "dark" ? "#fff" : "#1e293b", marginBottom: "6px" }}>
                {selectedFile.name}
              </div>
              <div style={{ fontSize: "14px", color: theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(30, 41, 59, 0.7)" }}>
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
            <button
              style={{ ...styles.closeButton, color: "#ef4444" }}
              onClick={() => setSelectedFile(null)}
              disabled={isUploading}
            >
              <XCircle size={24} />
            </button>
          </div>
        )}

        {/* Document Type Dropdown */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>{t.documentType}</label>
          <ReusableDropdown
            items={[
              { value: "contract", label: t.contract },
              { value: "payslip", label: t.payslip },
              { value: "certificate", label: t.certificate },
              { value: "policy", label: t.policy },
              { value: "other", label: t.other || (language === "ar" ? "أخرى" : "Other") },
            ]}
            value={selectedType || ""}
            onChange={(val) => {
              setSelectedType(val);
              if (val !== "other") setOtherDocumentName("");
            }}
            onSelect={(item) => setSelectedType(item.value)}
            placeholder={t.selectDocumentType}
            theme={theme}
            language={language}
            getItemLabel={(item) => item.label}
            getItemKey={(item) => item.value}
          />
        </div>

        {selectedType === "other" && (
          <div style={{ ...styles.formGroup, marginTop: "16px" }}>
            <label style={styles.formLabel}>{language === "ar" ? "اسم المستند" : "Document Name"}</label>
            <input
              type="text"
              value={otherDocumentName}
              onChange={(e) => setOtherDocumentName(e.target.value)}
              placeholder={language === "ar" ? "أدخل اسم المستند..." : "Enter document name..."}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "12px",
                border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid rgba(15, 23, 42, 0.1)",
                background: theme === "dark" ? "rgba(15, 23, 42, 0.5)" : "rgba(248, 250, 252, 0.8)",
                color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                fontSize: "14px",
                outline: "none",
                textAlign: language === "ar" ? "right" : "left",
                boxSizing: "border-box",
              }}
            />
          </div>
        )}

        {/* Actions */}
        <div style={styles.modalActions}>
          <button
            style={styles.secondaryButton}
            onClick={handleClose}
            disabled={isUploading}
          >
            {t.cancel}
          </button>
          <button
            style={{
              ...styles.primaryButton,
              opacity: !selectedFile || isUploading || (selectedType === "other" && !otherDocumentName.trim()) ? 0.6 : 1,
              cursor: !selectedFile || isUploading || (selectedType === "other" && !otherDocumentName.trim()) ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={() => handleUpload(selectedType === "other" ? otherDocumentName.trim() : selectedType)}
            disabled={!selectedFile || isUploading || (selectedType === "other" && !otherDocumentName.trim())}
          >
            {isUploading ? (
              <>
                <Loader size={20} style={{ animation: "spin 1s linear infinite" }} />
                {language === "ar" ? "جاري التحميل..." : "Uploading..."}
              </>
            ) : (
              <>
                <Upload size={20} />
                {t.upload}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;